import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Background from './Background';
import { assetUrl } from '../utils/assets';

function DNALoader() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let t = 0;
    let raf;
    function draw() {
      ctx.clearRect(0, 0, 160, 160);
      ctx.shadowBlur = 8;
      const steps = 40;
      for (let i = 0; i < steps; i++) {
        const pct = i / steps;
        const y = 20 + pct * 120;
        const phase = t + pct * Math.PI * 4;
        const x1 = 80 + 40 * Math.cos(phase);
        const x2 = 80 + 40 * Math.cos(phase + Math.PI);
        const z1 = Math.sin(phase);
        const z2 = Math.sin(phase + Math.PI);
        if (i % 4 === 0) {
          ctx.strokeStyle = `rgba(61,189,106,${0.2 + 0.3 * Math.abs(z1)})`;
          ctx.lineWidth = 1;
          ctx.shadowColor = 'transparent';
          ctx.beginPath();
          ctx.moveTo(x1, y);
          ctx.lineTo(x2, y);
          ctx.stroke();
        }
        const r1 = 2.5 + 1.5 * z1;
        const a1 = 0.3 + 0.7 * ((z1 + 1) / 2);
        ctx.shadowColor = z1 > 0 ? '#5eff9a' : 'transparent';
        ctx.fillStyle = z1 > 0 ? `rgba(94,255,154,${a1})` : `rgba(42,102,66,${a1})`;
        ctx.beginPath();
        ctx.arc(x1, y, Math.max(1, r1), 0, Math.PI * 2);
        ctx.fill();
        const r2 = 2.5 + 1.5 * z2;
        const a2 = 0.3 + 0.7 * ((z2 + 1) / 2);
        ctx.shadowColor = z2 > 0 ? '#5eff9a' : 'transparent';
        ctx.fillStyle = z2 > 0 ? `rgba(94,255,154,${a2})` : `rgba(42,102,66,${a2})`;
        ctx.beginPath();
        ctx.arc(x2, y, Math.max(1, r2), 0, Math.PI * 2);
        ctx.fill();
      }
      t += 0.04;
      raf = requestAnimationFrame(draw);
    }
    draw();
    return () => cancelAnimationFrame(raf);
  }, []);
  return <canvas ref={canvasRef} width={160} height={160} style={{ display: 'block' }} />;
}

function OrbitLoader() {
  return (
    <svg width="160" height="160" viewBox="0 0 160 160" style={{ display: 'block' }}>
      <defs>
        <filter id="glowO">
          <feGaussianBlur stdDeviation="3" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <path id="orbit55" d="M 80,25 a 55,55 0 1,1 -0.01,0 Z"/>
        <path id="orbit35" d="M 80,45 a 35,35 0 1,1 -0.01,0 Z"/>
        <path id="orbit18" d="M 80,62 a 18,18 0 1,1 -0.01,0 Z"/>
      </defs>
      <circle cx="80" cy="80" r="55" fill="none" stroke="#161d17" strokeWidth="1" strokeDasharray="4 6"/>
      <circle cx="80" cy="80" r="35" fill="none" stroke="#161d17" strokeWidth="1" strokeDasharray="3 5"/>
      <circle cx="80" cy="80" r="18" fill="none" stroke="#161d17" strokeWidth="1" strokeDasharray="2 4"/>
      <g filter="url(#glowO)">
        <circle r="5" fill="#3dbd6a"><animateMotion dur="3s" repeatCount="indefinite"><mpath href="#orbit55"/></animateMotion></circle>
        <circle r="3.5" fill="#2a6642" opacity="0.7"><animateMotion dur="3s" begin="-1s" repeatCount="indefinite"><mpath href="#orbit55"/></animateMotion></circle>
        <circle r="2" fill="#1a3d28" opacity="0.5"><animateMotion dur="3s" begin="-2s" repeatCount="indefinite"><mpath href="#orbit55"/></animateMotion></circle>
        <circle r="4" fill="#5eff9a"><animateMotion dur="2s" begin="-0.5s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear"><mpath href="#orbit35"/></animateMotion></circle>
        <circle r="2.5" fill="#3dbd6a" opacity="0.6"><animateMotion dur="2s" begin="-1.5s" repeatCount="indefinite" keyPoints="1;0" keyTimes="0;1" calcMode="linear"><mpath href="#orbit35"/></animateMotion></circle>
        <circle r="3" fill="#5eff9a"><animateMotion dur="1.2s" repeatCount="indefinite"><mpath href="#orbit18"/></animateMotion></circle>
      </g>
      <circle cx="80" cy="80" r="2.5" fill="#3dbd6a">
        <animate attributeName="opacity" values="1;0.2;1" dur="1.2s" repeatCount="indefinite"/>
      </circle>
    </svg>
  );
}

function PulseGridLoader() {
  const dots = [];
  const cols = 5, rows = 5, sp = 28, ox = 24, oy = 24;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      const cx = ox + c * sp;
      const cy = oy + r * sp;
      const delay = `${(i * 0.08) % 1.2}s`;
      dots.push(
        <circle key={i} cx={cx} cy={cy} r="4" fill="#3dbd6a" opacity="0.15">
          <animate attributeName="opacity" values="0.12;1;0.12" dur="1.6s" begin={delay} repeatCount="indefinite"/>
          <animate attributeName="r" values="2.5;5;2.5" dur="1.6s" begin={delay} repeatCount="indefinite"/>
        </circle>
      );
    }
  }
  return (
    <svg width="160" height="160" viewBox="0 0 160 160" style={{ display: 'block' }}>
      <defs>
        <filter id="glowP">
          <feGaussianBlur stdDeviation="2" result="blur"/>
          <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      <g filter="url(#glowP)">{dots}</g>
    </svg>
  );
}

/* Statue plate — <picture> so webp is served where supported. The alt is
   empty on decorative plates so screen readers skip the atmosphere.
   `fallback` is png for the alpha plates, jpg for the opaque ones. */
function Plate({ name, alt = '', fallback = 'jpg' }) {
  return (
    <picture>
      <source srcSet={assetUrl(`/images/${name}.webp`)} type="image/webp" />
      <img src={assetUrl(`/images/${name}.${fallback}`)} alt={alt} loading="lazy" decoding="async" />
    </picture>
  );
}

const pillars = [
  {
    title: 'Automation',
    desc: 'End-to-end automation.',
    art: <DNALoader />
  },
  {
    title: 'Technical Consulting',
    desc: 'Expert guidance across the digital landscape.',
    art: <OrbitLoader />
  },
  {
    title: 'Development & Managed Services',
    desc: 'Custom platforms, applications, and managed technology.',
    art: <PulseGridLoader />
  }
];

const Landing = () => {
  return (
    <div className="landing-page lp">

      {/* ── 01 · Overture — the ASCII mark, Victory behind it ── */}
      <section className="lp-overture">
        <div className="lp-overture-figure gd gd-soft gd-portrait gd-drift" aria-hidden="true">
          <Plate name="nike-victory" />
        </div>
        <div className="lp-overture-inner">
          <Background />
        </div>
        <div className="lp-scroll-cue" aria-hidden="true">
          <span />
          SCROLL
        </div>
      </section>

      {/* ── 02 · Hero ── */}
      <section className="lp-hero">
        <div className="lp-shell lp-hero-grid">
          <div className="lp-hero-copy">
            <h1 className="lp-display">
              <em>Pixionary</em>
            </h1>
            <span className="lp-eyebrow lp-eyebrow-sub">Latest Development</span>
            <div className="lp-cta-row">
              <a href="https://pixionary.net" target="_blank" rel="noopener noreferrer" className="lp-btn lp-btn-ghost">
                Check it out →
              </a>
            </div>
          </div>

          <div className="lp-hero-figure gd gd-portrait gd-drift">
            <div className="lp-halo" aria-hidden="true" />
            <Plate name="nike-wreath" fallback="png" alt="Winged Victory raising a laurel wreath" />
          </div>
        </div>
      </section>

      {/* ── 03 · Pillars ── */}
      <section className="lp-pillars">
        <div className="lp-pillars-figure gd gd-bleed-left gd-drift" aria-hidden="true">
          <Plate name="athena-gold" />
        </div>
        <div className="lp-shell">
          <div className="lp-pillars-head">
            <h2 className="lp-section-title">What we build &amp; manage</h2>
            <Link to="/services" className="lp-btn lp-btn-ghost">All Services →</Link>
          </div>

          <div className="lp-pillar-grid">
            {pillars.map((pillar, i) => (
              <article className="lp-pillar" key={pillar.title}>
                <span className="lp-pillar-idx">{String(i + 1).padStart(2, '0')}</span>
                <div className="lp-pillar-art" aria-hidden="true">{pillar.art}</div>
                <h3>{pillar.title}</h3>
                {pillar.desc && <p>{pillar.desc}</p>}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 04 · Aegis — Athena statement band ── */}
      <section className="lp-aegis">
        <div className="lp-aegis-figure gd gd-cover gd-bleed-right" aria-hidden="true">
          <Plate name="diana-cupid" />
        </div>
        <div className="lp-shell">
          <div className="lp-aegis-inner">
            <span className="lp-eyebrow">The Tavernix Approach</span>
            <p className="lp-statement">
              Built for the way you operate.
            </p>
            <p className="lp-aegis-body">
              We design, implement, and manage technology that moves you forward.
            </p>
            <p className="lp-aegis-tag">Streamline workflows. Tailored solutions.</p>
          </div>
        </div>
      </section>

      {/* ── 05 · Technical Solutions ── */}
      <section className="lp-voice">
        <div className="lp-voice-figure gd gd-portrait gd-bleed-left gd-drift" aria-hidden="true">
          <Plate name="themis" fallback="png" />
        </div>
        <div className="lp-shell">
          <div className="lp-voice-inner">
            <span className="lp-eyebrow">Technology &amp; Systems</span>
            <h2 className="lp-section-title">From infrastructure to implementation.</h2>
            <p className="lp-aegis-body">
              Practical technical expertise across systems
            </p>
            <ul className="lp-voice-list">
              <li>
                <b>01</b>
                <span><strong>Systems &amp; Infrastructure</strong>Networks, servers, infrastructure, and deployment.</span>
              </li>
              <li>
                <b>02</b>
                <span><strong>Automation &amp; Integration</strong>AI integration, connected systems, and workflow automation.</span>
              </li>
              <li>
                <b>03</b>
                <span><strong>Development &amp; Managed Services</strong>Custom tools, ongoing maintenance, and technical support.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* ── 06 · Invoke — closing call ── */}
      <section className="lp-invoke">
        <div className="lp-invoke-figure gd gd-soft" aria-hidden="true">
          <Plate name="athena-flight" />
        </div>
        <div className="lp-shell lp-invoke-inner">
          <h2>Get in touch.</h2>
          <p>Have something in mind?</p>
          <div className="lp-cta-row">
            <a href="mailto:info@tavernix.io" className="lp-btn lp-btn-ghost">
              Contact us →
            </a>
          </div>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-shell lp-footer-inner">
          <span>&copy; 2026 Tavernix Systems // Est_2024</span>
          <a href="mailto:info@tavernix.io">info@tavernix.io</a>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
